//src/pages/DocumentsPage.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Download,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  Loader2,
  LockKeyhole,
  Search,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { PermissionIconButton } from "@/components/shared/PermissionIconButton";
import { PageHeader } from "@/components/shared/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import {
  deleteDocument,
  downloadDocument,
  getDocumentStorageSummary,
  listDocuments,
  uploadDocument,
} from "@/services/documents";
import type { DocumentFileType, DocumentItem, DocumentStorageSummary } from "@/types/documents";

const ACCEPTED_TYPES = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png";
const PAGE_SIZE = 30;

type PendingDocument = {
  id: string;
  file: File;
  title: string;
  description: string;
  status: "pending" | "uploading" | "error";
  error?: string;
};

function formatBytes(bytes: number) {
  if (!bytes) return "0 MB";
  const mb = bytes / 1024 / 1024;
  if (mb >= 1) return `${mb.toFixed(2).replace(".", ",")} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(1).replace(".", ",")} KB`;
}

function formatDate(value: string) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function getFileMeta(document: Pick<DocumentItem, "file_extension" | "file_name"> | { file_name: string; file_extension?: string | null }) {
  const ext = (document.file_extension || document.file_name.split(".").pop() || "").toLowerCase();
  if (["jpg", "jpeg", "png", ".jpg", ".jpeg", ".png"].includes(ext)) {
    return { label: "Imagem", icon: FileImage, badge: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  }
  if (["xls", "xlsx", ".xls", ".xlsx"].includes(ext)) {
    return { label: "Excel", icon: FileSpreadsheet, badge: "bg-green-50 text-green-700 border-green-200" };
  }
  if (["doc", "docx", ".doc", ".docx"].includes(ext)) {
    return { label: "Word", icon: FileText, badge: "bg-blue-50 text-blue-700 border-blue-200" };
  }
  if (["pdf", ".pdf"].includes(ext)) {
    return { label: "PDF", icon: FileArchive, badge: "bg-red-50 text-red-700 border-red-200" };
  }
  return { label: "Arquivo", icon: FileText, badge: "bg-slate-50 text-slate-700 border-slate-200" };
}

function saveBlob(blob: Blob, filename?: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "documento";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function titleFromFileName(fileName: string) {
  const cleanName = (fileName || "Documento").replace(/\.[^/.]+$/, "").replace(/[_-]+/g, " ").trim();
  return cleanName || "Documento";
}

function makePendingDocument(file: File): PendingDocument {
  return {
    id: `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`,
    file,
    title: titleFromFileName(file.name),
    description: "",
    status: "pending",
  };
}

function errorToMessage(error: any) {
  const message = error?.message || "Erro ao enviar documento";
  if (message === "document_storage_limit_exceeded") {
    return "Limite de armazenamento excedido para este tenant.";
  }
  if (message === "document_extension_not_allowed" || message === "document_mime_type_not_allowed") {
    return "Tipo de arquivo não permitido. Envie PDF, Word, Excel, JPG ou PNG.";
  }
  if (message === "document_title_required") {
    return "Informe o título do documento.";
  }
  if (message === "document_title_too_long") {
    return "O título deve ter no máximo 255 caracteres.";
  }
  return message;
}

export default function DocumentsPage() {
  const { user } = useAuth();
  const canWrite = user?.permissions?.includes(PERMISSIONS.DOCUMENTS_WRITE) ?? false;

  const inputRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [storage, setStorage] = useState<DocumentStorageSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [fileType, setFileType] = useState<DocumentFileType>("all");
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DocumentItem | null>(null);

  const usedPercentage = Math.min(storage?.percentage || 0, 100);
  const hasNextPage = offset + PAGE_SIZE < total;
  const hasPreviousPage = offset > 0;
  const hasPendingDocuments = pendingDocuments.length > 0;
  const pendingWithoutTitle = pendingDocuments.some((item) => !item.title.trim());

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [list, summary] = await Promise.all([
        listDocuments({ q: search, file_type: fileType, limit: PAGE_SIZE, offset }),
        getDocumentStorageSummary(),
      ]);
      setDocuments(list.items || []);
      setTotal(list.total || 0);
      setStorage(summary);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar documentos");
    } finally {
      setIsLoading(false);
    }
  }, [fileType, offset, search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadData();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [loadData]);

  const pendingTotalSize = useMemo(
    () => pendingDocuments.reduce((sum, item) => sum + item.file.size, 0),
    [pendingDocuments],
  );

  function addFilesToPending(files: FileList | File[]) {
    if (!canWrite || isUploading) return;

    const list = Array.from(files || []);
    if (!list.length) return;

    setPendingDocuments((current) => [...current, ...list.map(makePendingDocument)]);
    if (inputRef.current) inputRef.current.value = "";
  }

  function updatePendingDocument(id: string, patch: Partial<Pick<PendingDocument, "title" | "description">>) {
    setPendingDocuments((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch, error: undefined, status: "pending" } : item)),
    );
  }

  function removePendingDocument(id: string) {
    if (isUploading) return;
    setPendingDocuments((current) => current.filter((item) => item.id !== id));
  }

  async function handleUploadPending() {
    if (!canWrite || isUploading) return;

    if (!pendingDocuments.length) {
      toast.info("Selecione ao menos um documento para enviar.");
      return;
    }

    if (pendingWithoutTitle) {
      toast.error("Informe o título de todos os documentos antes de enviar.");
      return;
    }

    setIsUploading(true);
    const successfulIds = new Set<string>();
    let failCount = 0;

    for (const item of pendingDocuments) {
      setPendingDocuments((current) =>
        current.map((doc) => (doc.id === item.id ? { ...doc, status: "uploading", error: undefined } : doc)),
      );

      try {
        await uploadDocument(item.file, item.title, item.description);
        successfulIds.add(item.id);
      } catch (error: any) {
        failCount += 1;
        const message = errorToMessage(error);
        setPendingDocuments((current) =>
          current.map((doc) => (doc.id === item.id ? { ...doc, status: "error", error: message } : doc)),
        );
      }
    }

    setPendingDocuments((current) => current.filter((item) => !successfulIds.has(item.id)));
    setIsUploading(false);

    if (successfulIds.size > 0) {
      toast.success(
        successfulIds.size === 1
          ? "Documento enviado com sucesso"
          : `${successfulIds.size} documentos enviados com sucesso`,
      );
      setOffset(0);
      await loadData();
    }

    if (failCount > 0) {
      toast.error(`${failCount} documento(s) não foram enviados. Verifique os erros na fila.`);
    }
  }

  async function handleDownload(documentItem: DocumentItem) {
    try {
      const response = await downloadDocument(documentItem.id);
      saveBlob(response.blob, response.filename || documentItem.file_name);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao baixar documento");
    }
  }

  async function handleDelete() {
    if (!deleteTarget || !canWrite) return;
    try {
      await deleteDocument(deleteTarget.id);
      toast.success("Documento removido definitivamente");
      setDeleteTarget(null);
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao remover documento");
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documentos"
        description="Gerencie arquivos, evidências e documentos corporativos do tenant."
      >
        <PermissionButton
          permission={PERMISSIONS.DOCUMENTS_WRITE}
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <UploadCloud className="mr-2 h-4 w-4" />
          )}
          Selecionar arquivos
        </PermissionButton>
      </PageHeader>

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        {canWrite ? (
          <Card className="overflow-hidden border-dashed">
            <CardContent className="p-0">
              <div
                className={`flex min-h-[220px] cursor-pointer flex-col items-center justify-center gap-4 p-8 text-center transition-colors ${
                  isDragging ? "bg-primary/10" : "bg-muted/20 hover:bg-muted/40"
                }`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setIsDragging(false);
                  addFilesToPending(event.dataTransfer.files);
                }}
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  {isUploading ? <Loader2 className="h-8 w-8 animate-spin" /> : <UploadCloud className="h-8 w-8" />}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Arraste arquivos aqui ou clique para selecionar</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    PDF, Word, Excel, JPG e PNG. Os arquivos entram em uma fila para você revisar título e descrição antes de enviar.
                  </p>
                </div>
                {hasPendingDocuments ? (
                  <Badge variant="secondary">
                    {pendingDocuments.length} arquivo(s) na fila • {formatBytes(pendingTotalSize)}
                  </Badge>
                ) : null}
              </div>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={ACCEPTED_TYPES}
                className="hidden"
                disabled={isUploading}
                onChange={(event) => event.target.files && addFilesToPending(event.target.files)}
              />
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden border-dashed">
            <CardContent className="flex min-h-[220px] flex-col items-center justify-center gap-4 bg-muted/20 p-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <LockKeyhole className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Acesso somente leitura</h3>
                <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                  Você pode visualizar e baixar documentos, mas não possui permissão para enviar ou excluir arquivos.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Armazenamento</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-medium">Uso atual</span>
                <span className="text-muted-foreground">{usedPercentage.toFixed(1).replace(".", ",")}%</span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${usedPercentage}%` }} />
              </div>
            </div>
            <div className="rounded-xl bg-muted/40 p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Utilizado</span>
                <strong>{formatBytes(storage?.used_bytes || 0)}</strong>
              </div>
              <div className="mt-2 flex justify-between">
                <span className="text-muted-foreground">Limite</span>
                <strong>{storage?.limit_mb || 50} MB</strong>
              </div>
              <div className="mt-2 flex justify-between">
                <span className="text-muted-foreground">Disponível</span>
                <strong>{formatBytes(storage?.remaining_bytes || 0)}</strong>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {canWrite && hasPendingDocuments ? (
        <Card className="border-primary/20">
          <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="text-base">Documentos pendentes para envio</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Revise o título obrigatório e, se necessário, adicione uma descrição para facilitar a consulta futura.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setPendingDocuments([])} disabled={isUploading}>
                Limpar seleção
              </Button>
              <Button onClick={handleUploadPending} disabled={isUploading || pendingWithoutTitle}>
                {isUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
                Enviar documentos
              </Button>
            </div>
          </CardHeader>

          <CardContent className="space-y-3">
            {pendingDocuments.map((item) => {
              const meta = getFileMeta({ file_name: item.file.name });
              const Icon = meta.icon;
              const isItemUploading = item.status === "uploading";

              return (
                <div key={item.id} className="rounded-2xl border bg-card p-4">
                  <div className="grid gap-4 lg:grid-cols-[1fr_1.5fr_auto] lg:items-start">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted">
                        {isItemUploading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <Icon className="h-5 w-5 text-primary" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="break-all text-sm font-medium">{item.file.name}</p>
                          <Badge variant="outline" className={meta.badge}>{meta.label}</Badge>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{formatBytes(item.file.size)}</p>
                        {item.error ? <p className="mt-2 text-xs font-medium text-destructive">{item.error}</p> : null}
                      </div>
                    </div>

                    <div className="grid gap-3">
                      <div>
                        <label className="text-sm font-medium">Título do documento *</label>
                        <Input
                          className="mt-1"
                          value={item.title}
                          onChange={(event) => updatePendingDocument(item.id, { title: event.target.value })}
                          placeholder="Ex.: Laudo psicossocial 2026"
                          disabled={isUploading}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Descrição</label>
                        <textarea
                          className="mt-1 min-h-[76px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          value={item.description}
                          onChange={(event) => updatePendingDocument(item.id, { description: event.target.value })}
                          placeholder="Descreva brevemente o conteúdo do arquivo."
                          disabled={isUploading}
                        />
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removePendingDocument(item.id)}
                      disabled={isUploading}
                      className="justify-self-end"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <CardTitle className="text-base">Arquivos enviados</CardTitle>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative min-w-[260px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setOffset(0);
                  }}
                  placeholder="Buscar por título, arquivo ou descrição..."
                  className="pl-9"
                />
              </div>
              <Select
                value={fileType}
                onValueChange={(value) => {
                  setFileType(value as DocumentFileType);
                  setOffset(0);
                }}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="word">Word</SelectItem>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="image">Imagens</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex min-h-[220px] items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Carregando documentos...
            </div>
          ) : documents.length === 0 ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed text-center">
              <FileText className="h-10 w-10 text-muted-foreground" />
              <h3 className="mt-3 font-semibold">Nenhum documento encontrado</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {canWrite ? "Envie o primeiro arquivo ou ajuste os filtros de busca." : "Ajuste os filtros de busca ou aguarde um documento ser enviado."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((documentItem) => {
                const meta = getFileMeta(documentItem);
                const Icon = meta.icon;
                return (
                  <div
                    key={documentItem.id}
                    className="flex flex-col gap-4 rounded-xl border bg-card p-4 transition-colors hover:border-primary/30 md:flex-row md:items-center"
                  >
                    <div className="flex flex-1 items-start gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="break-words text-sm font-semibold">{documentItem.title || documentItem.file_name}</h3>
                          <Badge variant="outline" className={meta.badge}>{meta.label}</Badge>
                        </div>
                        <p className="mt-1 break-all text-xs text-muted-foreground">
                          Arquivo original: {documentItem.original_file_name || documentItem.file_name}
                        </p>
                        {documentItem.description ? (
                          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{documentItem.description}</p>
                        ) : null}
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span>{formatBytes(documentItem.file_size_bytes)}</span>
                          <span>Enviado em {formatDate(documentItem.uploaded_at)}</span>
                          {documentItem.uploaded_by_name ? <span>Por {documentItem.uploaded_by_name}</span> : null}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 md:justify-end">
                      <Button variant="outline" size="sm" onClick={() => handleDownload(documentItem)}>
                        <Download className="mr-2 h-4 w-4" />
                        Baixar
                      </Button>
                      <PermissionIconButton
                        label="Excluir"
                        permission={PERMISSIONS.DOCUMENTS_WRITE}
                        onClick={() => setDeleteTarget(documentItem)}
                        destructive
                      >
                        <Trash2 className="h-4 w-4" />
                      </PermissionIconButton>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 flex flex-col gap-3 border-t pt-4 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <span>
              Exibindo {documents.length} de {total} documento(s)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!hasPreviousPage || isLoading}
                onClick={() => setOffset((value) => Math.max(value - PAGE_SIZE, 0))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasNextPage || isLoading}
                onClick={() => setOffset((value) => value + PAGE_SIZE)}
              >
                Próxima
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Excluir documento definitivamente"
        description={
          deleteTarget
            ? `Deseja excluir definitivamente o documento "${deleteTarget.title || deleteTarget.file_name}"? O arquivo físico será removido do servidor e o espaço será liberado.`
            : "Deseja excluir definitivamente este documento?"
        }
        confirmLabel="Excluir definitivamente"
        onConfirm={handleDelete}
      />
    </div>
  );
}
